import { makeStyles } from '@material-ui/core/styles';
import {
  Typography, Paper, TableRow, TableHead, TableContainer,
  TableCell, TableBody, Table, Accordion, AccordionSummary, AccordionDetails,
  Grid,
  Box
} from '@material-ui/core';
import { observer } from 'mobx-react';
import { transformDataV2 } from './foreclosureOverviewUtils';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { formatCurrency } from '@roc/ui/utils';
import { DateFormat, formatDate } from '@roc/feature-utils';

const useStyles = makeStyles({
  table: {
    minWidth: 650,
    '& .MuiTableRow-head th': {
      fontWeight: 'bold',
    },
  },
  tableContainer: {
    maxHeight: 800,
  },
  accordionSummary: {
    backgroundColor: '#D9DADC',
    '& .MuiAccordionSummary-content': {
      margin: '10px 0px',
    },
  },
  labelRow: {
    backgroundColor: '#edebeb !important',
    paddingTop: '10px',
    paddingBottom: '10px',
  },
  noMarginBottom: {
    marginBottom: '0px',
  },
  accordionDetails: {
    padding: '0px',
  }
});

interface Props {
  title: string;
  columns: any[];
  data: any;
}

export const ValuationHistoryTable = observer(({ title, columns, data }: Props) => {
  const classes = useStyles();

  const getHeaderTableRow = (columns: any[], key: string) => {
    return (
      <TableRow key={`${key}-head-columns-row`}>
        {columns?.map((column) => (
          <TableCell key={`${key}-${column.fieldname}`} align="center">{column.fieldname}</TableCell>
        ))}
      </TableRow>
    );
  };

  const getLabelTableRow = (columns: any[], key: string) => {
    const lastIndex = columns.length - 1;
    return (
      <TableRow key={`${key}-head-label-row`}>
        {columns?.map((column, index) => {
          const rows = [...data[key]].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
          return (<TableCell className={classes.labelRow} key={`${key}-${index}-label`} align="center">
            {
              {
                "": key,
                "date": formatDate(rows[0]?.date, DateFormat.MMDDYYYY),
                "asIsValue": rows[0]?.asIsValue ? formatCurrency(rows[0]?.asIsValue, 2) : 'N/A',
                "afterRepairValue": rows[0]?.afterRepairValue ? formatCurrency(rows[0]?.afterRepairValue, 2) : 'N/A',
              }
              [column.mapvalue]
            }
          </TableCell>
          )
        })}
      </TableRow>
    );
  };

  const getBodyTableRow = (key: string, data: any) => {
    return (
      <>
        {[...data[key]].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())?.map((record: any, index: number) => (
          <TableRow hover key={`${key}-${index}-content-row`}>
            {transformDataV2(record, columns).map((item) => (
              <TableCell key={`${key}-${index}-${item.name}`} align="center">{item.value}</TableCell>
            ))}
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <Paper elevation={0} variant='outlined'>
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
          className={classes.accordionSummary}
        >
          <Typography gutterBottom variant="h5" component="h2" className={classes.noMarginBottom}>
            {title}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <TableContainer className={classes.tableContainer}>
            <Table className={classes.table} size='small' stickyHeader>
              {Object.keys(data).length ? Object.keys(data).map((key) => (
                <>
                  <TableHead key={`${key}-head-label`}>
                    {getLabelTableRow(columns, key)}
                  </TableHead>
                  <TableHead key={`${key}-head-columns`}>
                    {getHeaderTableRow(columns, key)}
                  </TableHead>
                  <TableBody key={`${key}-body-rows`}>
                    {getBodyTableRow(key, data)}
                  </TableBody>
                </>
              )) :
                <Grid item xs={12}>
                  <Box textAlign="center" paddingY={2}>No records.</Box>
                </Grid>
              }
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
});